'use client';

import { FormField } from '@shared/components/shadcn';
import { ChatTyping, Email, Phone } from '@shared/icons';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ChannelPreferences } from '../../../ChannelPreferences';
import { useMultistepForm } from '../../../form-controls/MultistepForm/hooks/useMultistepForm';
import type { ToggleOption } from '../../../form-controls/ToggleGroup/ToggleGroup';
import { SkipConfirmationDialog } from '../components/SkipConfirmationDialog/SkipConfirmationDialog';
import type { FormValues } from '../types';
import { setRegistrationFailureKind } from '../utils/registrationFailureKind';

function ChannelPreferencesStep() {
  const t = useTranslations('register.screens.channelPreferences');
  const actions = useTranslations('register.actions');
  const { skipToStep, setIsLoading, setStatus } = useMultistepForm();
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const form = useFormContext<FormValues>();

  const communicationOptions: ToggleOption[] = [
    { value: 'email', label: t('email'), icon: <Email />, trackingProps: { 'data-track-name': 'Email' } },
    { value: 'sms', label: t('sms'), icon: <ChatTyping />, trackingProps: { 'data-track-name': 'SMS' } },
    { value: 'phone', label: t('phonecall'), icon: <Phone />, trackingProps: { 'data-track-name': 'Phone' } },
  ];

  const [selectedPreferences, setSelectedPreferences] = useState<string[]>([]);

  const watchedPreferences = form.watch('channelPreferences');
  useEffect(() => {
    const prefs = (watchedPreferences ?? []).filter((pref: unknown): pref is string => typeof pref === 'string');
    setSelectedPreferences(prefs);
  }, [watchedPreferences]);

  const primaryButtonText = selectedPreferences.length === 0 ? t('selectAllAndContinue') : t('continueWithSelection');

  const handleCommunicationSubmit = async () => {
    const currentPreferences = form.getValues('channelPreferences') || [];

    if (currentPreferences.length === 0) {
      const allValues = communicationOptions.map(option => option.value);
      form.setValue('channelPreferences', allValues, { shouldValidate: false });
      setSelectedPreferences(allValues);

      skipToStep('deposit-limits');
    } else {
      skipToStep('deposit-limits');
    }
  };

  const handleSkipOffers = () => {
    setShowSkipDialog(true);
  };

  const handleConfirmSkip = async () => {
    try {
      setIsLoading(true);
      setShowSkipDialog(false);

      form.setValue('productPreferences', [], { shouldValidate: false });
      form.setValue('channelPreferences', [], { shouldValidate: false });
      setSelectedPreferences([]);

      setStatus('opted-out-preferences');
      skipToStep('deposit-limits');
    } catch {
      setRegistrationFailureKind('system');
      setStatus('error');
      skipToStep('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSkip = () => {
    setShowSkipDialog(false);
  };

  const errorMessage = form.formState.errors.channelPreferences?.message;

  return (
    <>
      <FormField
        control={form.control}
        name="channelPreferences"
        render={({ field }) => (
          <ChannelPreferences
            value={field.value ?? []}
            onChange={field.onChange}
            options={communicationOptions}
            error={errorMessage?.toString()}
            onContinue={handleCommunicationSubmit}
            onSkipOffers={handleSkipOffers}
            continueButtonText={primaryButtonText}
            skipButtonText={actions('dontSend')}
            continueButtonTrackName={selectedPreferences.length === 0 ? 'All Options' : 'Selected Options'}
            fieldId="channelPreferences-error"
            fieldTrackHelper="Products"
            stepLayout={{
              title: t('title'),
              'data-testid': 'channel-preferences-step',
              showLogo: true,
              showBackButton: true,
            }}
          />
        )}
      />

      <SkipConfirmationDialog isOpen={showSkipDialog} onConfirm={handleConfirmSkip} onCancel={handleCancelSkip} />
    </>
  );
}

export { ChannelPreferencesStep };
